import {
  Award,
  BookOpen,
  CalendarClock,
  ChartArea,
  Dot,
  FileText,
  FolderKey,
  FolderOpen,
  Gauge,
  LayoutDashboard,
  ListChecks,
  PieChart,
  Receipt,
  Settings,
  ShieldCheck,
  TrendingUp,
  Vault,
} from 'lucide-react'

export enum CaiProduct {
  INSURANCE = 'INSURANCE',
  VAULT = 'VAULT',
  BOOKKEEPER = 'BOOKKEEPER',
  CFO = 'CFO',
}

/** Sections inside Cai Bookkeeper. Nothing here exists yet — there is no nav to replicate. */
export enum BookkeeperTab {
  OVERVIEW = 'OVERVIEW',
  TRANSACTIONS = 'TRANSACTIONS',
  REPORTS = 'REPORTS',
  TASKS = 'TASKS',
}

export enum ProductTab {
  INSURANCE = 'INSURANCE',
  COMPLIANCE = 'COMPLIANCE',
  FINANCE = 'FINANCE',
}

/** What a tab does when the account has none of its products. */
export enum DisabledTabTreatment {
  PITCH = 'PITCH',
  HIDDEN = 'HIDDEN',
}

export type IconComponent = React.ComponentType<{ className?: string }>

export interface NavRow {
  label: string
  icon: IconComponent
  /** Selected row — production marks these with font-semibold and a darkened icon. */
  active?: boolean
  /** Renders the row as an inert pitch rather than a place the customer can go. */
  locked?: boolean
  featured?: boolean
  tag?: string
}

export interface NavGroup {
  label?: string
  rows: NavRow[]
}

interface ProductCopy {
  label: string
  tag?: string
  pitch: string
  /** Defaults to the waitlist CTA; products with a real way in override it. */
  cta?: string
  /** Eligibility line under the CTA, for products the customer cannot simply buy. */
  note?: string
  /** Greyscale, secondary CTA — for a product that cannot be bought yet. */
  muted?: boolean
}

export const DEFAULT_PITCH_CTA = 'Get on the list'

export const PRODUCT_COPY: Record<CaiProduct, ProductCopy> = {
  [CaiProduct.INSURANCE]: {
    label: 'Insurance',
    pitch: 'Compare prices from carriers and get covered without a phone call.',
    cta: 'Get your quotes',
  },
  [CaiProduct.VAULT]: {
    label: 'Compliance vault',
    tag: 'New',
    pitch: 'Keep every compliance document current, without chasing paperwork.',
    cta: 'Set up my vault',
    note: 'Only available to Cai insurance customers',
  },
  [CaiProduct.BOOKKEEPER]: {
    label: 'Bookkeeping',
    pitch: 'Connect your accounts and let Cai keep the books current.',
    cta: 'Set up my books',
  },
  [CaiProduct.CFO]: {
    label: 'AI CFO',
    tag: 'Coming soon',
    pitch: 'See what every mile costs you and plan your next move.',
    muted: true,
  },
}

export const BOOKKEEPER_TAB_ORDER = [
  BookkeeperTab.OVERVIEW,
  BookkeeperTab.TRANSACTIONS,
  BookkeeperTab.REPORTS,
  BookkeeperTab.TASKS,
]

export const BOOKKEEPER_TAB_ROW: Record<BookkeeperTab, NavRow> = {
  [BookkeeperTab.OVERVIEW]: { label: 'Overview', icon: LayoutDashboard },
  [BookkeeperTab.TRANSACTIONS]: { label: 'Transactions', icon: Receipt },
  [BookkeeperTab.REPORTS]: { label: 'Reports', icon: PieChart },
  [BookkeeperTab.TASKS]: { label: 'Tasks', icon: ListChecks },
}

export const TAB_ORDER = [ProductTab.INSURANCE, ProductTab.COMPLIANCE, ProductTab.FINANCE]

export const TAB_LABEL: Record<ProductTab, string> = {
  [ProductTab.INSURANCE]: 'Insurance',
  [ProductTab.COMPLIANCE]: 'Compliance',
  [ProductTab.FINANCE]: 'Finance',
}

/** Insurance is sold by the agency; everything else is the parent company. */
export const TAB_ENTITY: Record<ProductTab, string> = {
  [ProductTab.INSURANCE]: 'Cai Insurance Services',
  [ProductTab.COMPLIANCE]: 'Cai Labs, Inc.',
  [ProductTab.FINANCE]: 'Cai Labs, Inc.',
}

/** Which products light a tab up — Finance carries two. */
export const TAB_PRODUCTS: Record<ProductTab, CaiProduct[]> = {
  [ProductTab.INSURANCE]: [CaiProduct.INSURANCE],
  [ProductTab.COMPLIANCE]: [CaiProduct.VAULT],
  [ProductTab.FINANCE]: [CaiProduct.BOOKKEEPER, CaiProduct.CFO],
}

export const DASHBOARD_ROW: NavRow = { label: 'Dashboard', icon: ChartArea, active: true }
export const DOCUMENTS_ROW: NavRow = { label: 'Documents', icon: FileText }
export const SETTINGS_ROW: NavRow = { label: 'Settings', icon: Settings }
export const VAULT_GROUP: NavGroup = {
  rows: [
    { label: 'Vault', icon: Vault, active: true },
    { label: 'Expiration timeline', icon: CalendarClock },
  ],
}

export const COVERAGE_GROUP: NavGroup = {
  label: 'Active coverage',
  rows: [
    { label: 'Insurance', icon: ShieldCheck },
    { label: 'Business info', icon: FolderKey },
    { label: 'Drivers', icon: Dot },
    { label: 'Trucks', icon: Dot },
    { label: 'Trailers', icon: Dot },
    { label: 'Loss runs', icon: Dot },
  ],
}

export const MANAGE_GROUP: NavGroup = {
  label: 'Manage',
  rows: [
    { label: 'Certificates (COI)', icon: Award },
    { label: 'Claims', icon: FolderOpen },
  ],
}

export const FINANCE_GROUP: NavGroup = {
  label: 'AI CFO',
  rows: [
    { label: 'Cash flow', icon: TrendingUp },
    { label: 'Forecast', icon: Gauge },
    { label: 'Benchmarks', icon: BookOpen },
  ],
}

/** The production customer nav, minus the Compliance vault — not a re-grouping of it. */
export const TABBED_INSURANCE_GROUPS: NavGroup[] = [
  { rows: [DASHBOARD_ROW] },
  COVERAGE_GROUP,
  MANAGE_GROUP,
  { rows: [DOCUMENTS_ROW] },
]

/**
 * Tab order follows the order products were bought — the first product a customer
 * came to Cai for takes the leftmost slot. Tabs with nothing behind them trail after.
 */
export function orderedTabs(products: CaiProduct[]): ProductTab[] {
  const owned: ProductTab[] = []
  for (const product of products) {
    const tab = TAB_ORDER.find((candidate) => TAB_PRODUCTS[candidate].includes(product))
    if (tab && !owned.includes(tab)) owned.push(tab)
  }
  return [...owned, ...TAB_ORDER.filter((tab) => !owned.includes(tab))]
}
